// Shared value-slot candidate policy — the emit pipeline (T7). Consumes the
// engine adapter's discovered descriptors and owns ambiguity (D9), qualifier
// policy (D8), bucketing/tiers (D6/D7), keywords (D13), and the no-relation-
// kinds invariant. Engine resolvers call this from their 'scalar-value'
// branch (T8–T12).

import type { AliasBinding, CompletionCandidate } from '@/core/engines';
import type { ValueSlotInfo } from '@/features/sql-grammar/model';
import { compatBucket } from './compat';
import { isDeadRoutineReturn } from './deadReturns';
import { emitValueSlotKeywords } from './keywords';
import {
  EXTRA_SOURCE_MIN_TIER,
  FK_PARTNER_ORDER_HINT,
  QUALIFIER_TIER,
  lhsIsResolved,
  routineCandidateTier,
  tierForColumn,
} from './tiers';
import type {
  ValueSlotColumnDescriptor,
  ValueSlotEngineAdapter,
  ValueSlotScope,
} from './types';

/** Relation-class kinds that must NEVER appear at a value slot. */
const FORBIDDEN_KINDS: ReadonlySet<string> = new Set([
  'table', 'view', 'schema', 'database', 'cte',
]);

/** C1 SF-2: pseudo-table and other legacy producers may lack `aliasSql` —
 *  the verbatim-spelling contract falls back to the parsed alias. */
function aliasSpelling(binding: AliasBinding): string {
  return binding.aliasSql ?? binding.alias;
}

export async function emitValueSlotCandidates(
  out: CompletionCandidate[],
  slot: ValueSlotInfo,
  adapter: ValueSlotEngineAdapter,
  scope: ValueSlotScope,
): Promise<void> {
  const columns = await adapter.collectScopedColumns();
  const resolved = lhsIsResolved(scope.lhsType);

  // D9 ambiguity: group by folded bare label; a unique name inserts bare,
  // a duplicated name emits one qualified candidate per owner, bare
  // suppressed. Type/FK metadata stays tied to the owning descriptor.
  const byName = new Map<string, ValueSlotColumnDescriptor[]>();
  for (const d of columns) {
    const key = d.label.toLowerCase();
    const group = byName.get(key);
    if (group) group.push(d);
    else byName.set(key, [d]);
  }
  for (const group of byName.values()) {
    const ambiguous = group.length > 1;
    for (const d of group) {
      const tier = tierForColumn(compatBucket(adapter.engine, scope.lhsType, d.coarseType), resolved);
      out.push({
        label: ambiguous ? d.insertQualified : d.label,
        insertText: ambiguous ? d.insertQualified : d.insertBare,
        isSnippet: false,
        kind: 'column',
        tier,
        detail: `${d.coarseType} column`,
        // Bounded-member cap exemption (T13a): scoped value-slot columns are
        // bounded by the STATEMENT's alias bindings, never the schema. A
        // demoted (T4/T5) column ranks below an unbounded snapshot-routine
        // flood (postgis ≈ 700 routines), and without this flag the untyped
        // 500-cap would silently DROP it — violating F8's "buckets demote,
        // they don't drop".
        capExempt: true,
        ...(d.fkPartnerOfLhs ? { orderHint: FK_PARTNER_ORDER_HINT } : {}),
      });
    }
  }

  // D8 qualifiers — one per distinct owner binding, kind 'alias' (a
  // non-relation kind so `<any-table>` negatives stay unconditional). The
  // verbatim alias spelling is both label and insertText; an aliased
  // relation's TABLE NAME never appears.
  const seenOwners = new Set<AliasBinding>();
  for (const d of columns) {
    if (seenOwners.has(d.owner)) continue;
    seenOwners.add(d.owner);
    const spelling = aliasSpelling(d.owner);
    out.push({
      label: spelling,
      insertText: spelling,
      isSnippet: false,
      kind: 'alias',
      tier: QUALIFIER_TIER,
      detail: d.owner.kind === 'table' || d.owner.kind === 'view'
        ? `${d.owner.relation} qualifier`
        : 'qualifier',
    });
  }

  // Routines — dead returns dropped (spec §A2, the F8 carve-out), the rest
  // tiered by return-type compat with underscore demotion (D7 + spec §A3).
  for (const r of adapter.collectScalarRoutines()) {
    if (isDeadRoutineReturn(r.returnsRaw)) continue;
    out.push({
      label: r.label,
      insertText: r.insertText,
      isSnippet: r.insertText.includes('$'),
      kind: 'routine',
      tier: routineCandidateTier(adapter.engine, scope.lhsType, resolved, r),
      detail: `returns ${r.returnsRaw ?? r.returnsCoarse}`,
      // C2 P2-5 — the clause paths' routine emitters all set this so
      // accepting a stored function opens Monaco parameter hints; the
      // scalar slot must not lose it.
      triggerSignatureHelp: true,
    });
  }

  emitValueSlotKeywords(out, {
    engine: adapter.engine,
    role: slot.role,
    operator: slot.operator,
    lhsBooleanish: scope.lhsBooleanish,
  });

  // Engine-owned extra scalar sources, tier-clamped to ≥3.
  if (adapter.extraScalarSources) {
    for (const c of adapter.extraScalarSources()) {
      if (FORBIDDEN_KINDS.has(c.kind)) continue; // legality is policy-owned
      out.push(c.tier < EXTRA_SOURCE_MIN_TIER ? { ...c, tier: EXTRA_SOURCE_MIN_TIER } : c);
    }
  }
}
